/**
 * SoundManager - Web Audio API based sound effects
 */

export class SoundManager {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;

  constructor() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (e) {
      console.warn('Web Audio API not supported', e);
    }
  }

  public async init(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  public play(type: string): void {
    if (!this.enabled || !this.audioContext) return;

    try {
      switch (type) {
        case 'fmWave':
          this.playFMWave();
          break;
        case 'laser':
          this.playLaser();
          break;
        case 'dualShot':
          this.playDualShot();
          break;
        case 'machineGun':
          this.playMachineGun();
          break;
        case 'shotgun':
          this.playShotgun();
          break;
        case 'superBlast':
          this.playSuperBlast();
          break;
        case 'enemyHit':
          this.playEnemyHit();
          break;
        case 'explosion':
          this.playExplosion();
          break;
        case 'playerHit':
          this.playPlayerHit();
          break;
        case 'powerup':
          this.playPowerUp();
          break;
        case 'waveComplete':
          this.playWaveComplete();
          break;
      }
    } catch (e) {
      console.warn('Sound playback error:', e);
    }
  }

  private playFMWave(): void {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    const filterNode = this.audioContext.createBiquadFilter();

    oscillator.connect(filterNode);
    filterNode.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.setValueAtTime(220, this.audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(880, this.audioContext.currentTime + 0.3);
    filterNode.frequency.setValueAtTime(2000, this.audioContext.currentTime);
    filterNode.Q.value = 5;
    filterNode.type = 'lowpass';

    gainNode.gain.setValueAtTime(0.15, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);
    oscillator.type = 'sawtooth';

    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + 0.3);
  }

  private playLaser(): void {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    const filterNode = this.audioContext.createBiquadFilter();

    oscillator.connect(filterNode);
    filterNode.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.setValueAtTime(1200, this.audioContext.currentTime);
    oscillator.frequency.linearRampToValueAtTime(1800, this.audioContext.currentTime + 0.05);
    filterNode.frequency.setValueAtTime(2000, this.audioContext.currentTime);
    filterNode.type = 'highpass';

    gainNode.gain.setValueAtTime(0.12, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.05);
    oscillator.type = 'sine';

    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + 0.05);
  }

  private playDualShot(): void {
    if (!this.audioContext) return;

    for (let i = 0; i < 2; i++) {
      setTimeout(() => {
        const oscillator = this.audioContext!.createOscillator();
        const gainNode = this.audioContext!.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext!.destination);

        oscillator.frequency.setValueAtTime(600, this.audioContext!.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(300, this.audioContext!.currentTime + 0.08);
        gainNode.gain.setValueAtTime(0.1, this.audioContext!.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext!.currentTime + 0.08);
        oscillator.type = 'square';

        oscillator.start();
        oscillator.stop(this.audioContext!.currentTime + 0.08);
      }, i * 30);
    }
  }

  private playMachineGun(): void {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.setValueAtTime(400, this.audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(200, this.audioContext.currentTime + 0.04);
    gainNode.gain.setValueAtTime(0.08, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.04);
    oscillator.type = 'square';

    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + 0.04);
  }

  private playShotgun(): void {
    if (!this.audioContext) return;

    // Create multiple oscillators for a more aggressive, layered sound
    for (let i = 0; i < 3; i++) {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      const filterNode = this.audioContext.createBiquadFilter();

      oscillator.connect(filterNode);
      filterNode.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Aggressive frequency sweep with harmonics
      const baseFreq = 200 - i * 30; // Different frequencies for each layer
      oscillator.frequency.setValueAtTime(baseFreq, this.audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(30, this.audioContext.currentTime + 0.1);

      // More aggressive filter
      filterNode.frequency.setValueAtTime(1200, this.audioContext.currentTime);
      filterNode.frequency.exponentialRampToValueAtTime(200, this.audioContext.currentTime + 0.1);
      filterNode.type = 'lowpass';
      filterNode.Q.setValueAtTime(2, this.audioContext.currentTime); // Add resonance

      // Louder and punchier
      const gain = 0.4 - i * 0.05; // Slightly different volumes
      gainNode.gain.setValueAtTime(gain, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);

      oscillator.type = 'sawtooth';

      oscillator.start(this.audioContext.currentTime + i * 0.01); // Slight delay between layers
      oscillator.stop(this.audioContext.currentTime + 0.1 + i * 0.01);
    }

    // Add a sharp attack noise burst
    const noiseOsc = this.audioContext.createOscillator();
    const noiseGain = this.audioContext.createGain();
    const noiseFilter = this.audioContext.createBiquadFilter();

    noiseOsc.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.audioContext.destination);

    noiseOsc.frequency.setValueAtTime(800, this.audioContext.currentTime);
    noiseFilter.frequency.setValueAtTime(2000, this.audioContext.currentTime);
    noiseFilter.type = 'highpass';

    noiseGain.gain.setValueAtTime(0.15, this.audioContext.currentTime);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.05);

    noiseOsc.type = 'square';
    noiseOsc.start();
    noiseOsc.stop(this.audioContext.currentTime + 0.05);
  }

  private playSuperBlast(): void {
    if (!this.audioContext) return;

    for (let i = 0; i < 3; i++) {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      const baseFreq = 800 - (i * 200);
      oscillator.frequency.setValueAtTime(baseFreq, this.audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(baseFreq * 0.5, this.audioContext.currentTime + 0.4);

      gainNode.gain.setValueAtTime(0.15, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.4);

      oscillator.type = ['sine', 'triangle', 'sawtooth'][i] as OscillatorType;
      oscillator.start(this.audioContext.currentTime + i * 0.05);
      oscillator.stop(this.audioContext.currentTime + 0.4 + i * 0.05);
    }
  }

  private playEnemyHit(): void {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(300, this.audioContext.currentTime + 0.1);
    gainNode.gain.setValueAtTime(0.15, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);
    oscillator.type = 'square';

    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + 0.1);
  }

  private playExplosion(): void {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    const filterNode = this.audioContext.createBiquadFilter();

    oscillator.connect(filterNode);
    filterNode.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.setValueAtTime(200, this.audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(50, this.audioContext.currentTime + 0.3);
    filterNode.frequency.setValueAtTime(500, this.audioContext.currentTime);
    filterNode.type = 'lowpass';

    gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);
    oscillator.type = 'sawtooth';

    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + 0.3);
  }

  private playPlayerHit(): void {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.setValueAtTime(200, this.audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(100, this.audioContext.currentTime + 0.3);
    gainNode.gain.setValueAtTime(0.2, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);
    oscillator.type = 'sawtooth';

    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + 0.3);
  }

  private playPowerUp(): void {
    if (!this.audioContext) return;

    for (let i = 0; i < 4; i++) {
      setTimeout(() => {
        const oscillator = this.audioContext!.createOscillator();
        const gainNode = this.audioContext!.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext!.destination);

        const freq = 440 * Math.pow(2, i / 4);
        oscillator.frequency.setValueAtTime(freq, this.audioContext!.currentTime);
        gainNode.gain.setValueAtTime(0.1, this.audioContext!.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext!.currentTime + 0.1);
        oscillator.type = 'sine';

        oscillator.start();
        oscillator.stop(this.audioContext!.currentTime + 0.1);
      }, i * 50);
    }
  }

  private playWaveComplete(): void {
    if (!this.audioContext) return;

    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        const oscillator = this.audioContext!.createOscillator();
        const gainNode = this.audioContext!.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext!.destination);

        const freq = 523 + (i * 100);
        oscillator.frequency.setValueAtTime(freq, this.audioContext!.currentTime);
        gainNode.gain.setValueAtTime(0.12, this.audioContext!.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext!.currentTime + 0.15);
        oscillator.type = 'triangle';

        oscillator.start();
        oscillator.stop(this.audioContext!.currentTime + 0.15);
      }, i * 60);
    }
  }
}
